import { useState } from 'react'
import { achsenSchritte, useBreite } from './diagramm'
import { Hinweis, HinweisZeile } from './Hinweis'

export interface LinienPunkt {
  schluessel: string
  etikett: string
  wert: number
  neben?: string
  ampel?: string | null
}

/**
 * Verlauf ueber die Zeit, mit Fadenkreuz.
 *
 * Punkte werden nur gezeichnet, wo sie sich nicht gegenseitig
 * verdecken - bei neunzig Tagen ist die Linie selbst die Aussage. Der
 * gezeigte Punkt entsteht erst beim Ueberfahren.
 */
export function Linie({
  punkte, hoehe = 220, farbe = 'var(--reihe-1)', formatiere, formatiereAchse,
}: {
  punkte: LinienPunkt[]
  hoehe?: number
  farbe?: string
  formatiere: (w: number) => string
  formatiereAchse: (w: number) => string
}) {
  const [ref, breite] = useBreite<HTMLDivElement>()
  const [aktiv, setAktiv] = useState<number | null>(null)
  const [lage, setLage] = useState({ x: 0, y: 0 })

  const links = 56, rechts = 12, oben = 16, unten = 26
  const zb = Math.max(0, breite - links - rechts)
  const zh = hoehe - oben - unten
  const max = Math.max(0, ...punkte.map((p) => p.wert))
  const { schritte, obergrenze } = achsenSchritte(max)
  const y = (v: number) => oben + zh * (1 - v / (obergrenze || 1))
  const x = (i: number) => punkte.length <= 1
    ? links + zb / 2
    : links + (zb * i) / (punkte.length - 1)

  const pfad = punkte.map((p, i) => `${i ? 'L' : 'M'}${x(i)} ${y(p.wert)}`).join(' ')
  const flaeche = punkte.length
    ? `${pfad} L${x(punkte.length - 1)} ${y(0)} L${x(0)} ${y(0)} Z` : ''

  // Beschriftet werden nur die Enden und der Hochpunkt. Alles andere
  // liest man am Fadenkreuz ab.
  const wichtig = new Set<number>()
  if (punkte.length) {
    wichtig.add(0); wichtig.add(punkte.length - 1)
    wichtig.add(punkte.reduce((b, p, i) => (p.wert > punkte[b].wert ? i : b), 0))
  }
  const zeigePunkte = punkte.length <= 40

  const beiBewegung = (e: React.MouseEvent<SVGRectElement>) => {
    const kasten = e.currentTarget.getBoundingClientRect()
    const rel = e.clientX - kasten.left
    const i = punkte.length <= 1 ? 0
      : Math.round(((rel) / (zb || 1)) * (punkte.length - 1))
    setAktiv(Math.max(0, Math.min(punkte.length - 1, i)))
    setLage({ x: e.clientX, y: e.clientY })
  }

  return (
    <div ref={ref}>
      {breite > 0 && punkte.length > 0 && (
        <svg className="diagramm" height={hoehe} role="img"
             aria-label={`Verlauf ueber ${punkte.length} Punkte`}>
          <defs>
            <linearGradient id="verlaufFlaeche" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={farbe} stopOpacity="0.16" />
              <stop offset="100%" stopColor={farbe} stopOpacity="0" />
            </linearGradient>
          </defs>
          {schritte.map((s) => (
            <g key={s}>
              <line className={s === 0 ? 'grundlinie' : 'gitterlinie'}
                    x1={links} x2={links + zb} y1={y(s)} y2={y(s)} />
              <text className="achsentext" x={links - 8} y={y(s) + 3}
                    textAnchor="end">{formatiereAchse(s)}</text>
            </g>
          ))}
          <path d={flaeche} fill="url(#verlaufFlaeche)" />
          <path className="marke-linie" d={pfad} stroke={farbe} />

          {zeigePunkte && punkte.map((p, i) => (
            <circle key={p.schluessel} cx={x(i)} cy={y(p.wert)} r={4}
                    fill={farbe} className="marke-trenner" />
          ))}

          {punkte.map((p, i) => wichtig.has(i) && (
            <text key={'e' + p.schluessel} className="achsentext"
                  x={x(i)} y={hoehe - 9}
                  textAnchor={i === 0 ? 'start' : i === punkte.length - 1 ? 'end' : 'middle'}>
              {p.etikett}
            </text>
          ))}

          {aktiv !== null && (
            <g>
              <line className="gitterlinie" x1={x(aktiv)} x2={x(aktiv)}
                    y1={oben} y2={y(0)} style={{ stroke: 'var(--linie-2)' }} />
              <circle cx={x(aktiv)} cy={y(punkte[aktiv].wert)} r={5}
                      fill={farbe} className="marke-trenner" />
            </g>
          )}

          <rect x={links} y={oben} width={zb} height={zh} fill="transparent"
                onMouseMove={beiBewegung} onMouseLeave={() => setAktiv(null)} />
        </svg>
      )}
      {aktiv !== null && punkte[aktiv] && (
        <Hinweis lage={lage}>
          <div className="titel">{punkte[aktiv].etikett}</div>
          <HinweisZeile name="Umsatz" wert={formatiere(punkte[aktiv].wert)} />
          {punkte[aktiv].neben && (
            <HinweisZeile name="Rechnungen" wert={punkte[aktiv].neben!} />
          )}
        </Hinweis>
      )}
    </div>
  )
}
